import { topologyConfig } from "components/Topology/configs/topologyConfig";
import { topologyColors } from "components/Topology/configs/topologyColors";
import { getValidActions } from "objs/_ConfiguratorObjs/Wizard/wizardHelpers";
import { getInteractiveElemKind } from "components/Topology/staticLayer/helpers";

const {
  nodeLinesGap,
  nodeWidth,
  nodeHeight,
  zoneTitleToLineGap,
  rightChildrenGapX,
  nodeGapY,
  bottomChildrenGapY,
  addBtnHeight,
  addBtnMarginTop,
  addBtnMarginLeft,
} = topologyConfig;

const { gray400 } = topologyColors;

export const computedZone = ({
  isPrint,
  famStartX,
  famStartY,
  famData,
  zoneIdx,
  selectedTopologyItemKind,
}) => {
  const {
    id = "",
    kind = "",
    title = "",
    info = "",
    parentConnectionType = "",
    rightPlacedChildren = [],
    bottomPlacedChildren = [],
    chainedChildren = [],
    menu = [],
  } = famData;

  const nodes = [];
  const texts = [];
  const twoAngleLines = [];
  const bottomLeftAngleLines = [];
  const leftBracketPolyLines = [];
  const interactiveElementsArr = [];

  const isWirelessZone = famData?.parentConnectionType === "WIRELESS";
  let zoneHeight = famStartY || 0;

  // Zone title
  interactiveElementsArr.push({
    ...famData,
    interactiveKind: "zoneBtn",
    startX: famStartX + 53,
    startY: famStartY - nodeHeight + 12,
    zoneIdx,
    title: info,
    fs: 18,
    fw: "bold",
    color: "#4f5155",
    dominantBaseline: "central",
    textAnchor: "end",
    alignmentBaseline: "alphabetic",
    isWirelessZone,
  });

  if (isPrint) {
    texts.push({
      x: famStartX + 53,
      y: famStartY - zoneTitleToLineGap,
      text: info,
      styles: {
        fs: 18,
        fw: "bold",
        anchor: "end",
      },
    });
  }

  // FAM node
  nodes.push({
    id,
    kind,
    title,
    subTitle: famData.presentation,
    iconName: famData.iconName,
    selectedAccessories: famData?.selectedAccessories,
    parentConnectionType,
    x: famStartX,
    y: famStartY,
    isPrint,
  });

  interactiveElementsArr.push({
    ...famData,
    interactiveKind: "node",
    startX: famStartX,
    startY: famStartY,
    zoneIdx,
  });

  const addComponentBtn = !isPrint && menu.find((btn) => btn.id === 12);

  //  FAM right children
  const rightPlacedChildrenCount = famData?.rightPlacedChildren?.length || 0;

  if (rightPlacedChildrenCount) {
    rightPlacedChildren.forEach((famChild, famChildIdx) => {
      const childStartX = famStartX + nodeWidth + rightChildrenGapX;
      const childStartY = zoneHeight + nodeHeight;
      const isWireless = famChild?.parentConnectionType === "WIRELESS";

      const {
        isPasteEnabled: isPasteFamChildEnabled,
        isPastConfigEnabled: isPastConfigFamChildEnabled,
      } = !isPrint && getValidActions(famChild.menu);

      const interactiveKind = getInteractiveElemKind(
        isPasteFamChildEnabled,
        isPastConfigFamChildEnabled,
      );

      nodes.push({
        id: famChild.id,
        kind: famChild.kind,
        title: famChild.title,
        subTitle: famChild.presentation,
        selectedAccessories: famChild?.selectedAccessories,
        iconName: famChild.iconName,
        x: childStartX,
        y: zoneHeight,
        isPrint,
      });

      interactiveElementsArr.push({
        ...famChild,
        interactiveKind,
        startX: childStartX,
        startY: zoneHeight,
        zoneIdx,
        topologyNodeId: famChild.id,
      });

      twoAngleLines.push({
        from: { x: famStartX + nodeWidth, y: famStartY + nodeHeight / 2 },
        to: { x: childStartX, y: childStartY - nodeHeight / 2 },
        lineIdx: famChildIdx,
        linesCount: rightPlacedChildrenCount,
        isWireless,
      });

      if (famChild?.directlyConnected?.length) {
        famChild.directlyConnected.forEach(
          (directlyConnectedChild, directlyConnectedChildIdx) => {
            nodes.push({
              id: directlyConnectedChild.id,
              kind: directlyConnectedChild.kind,
              title: directlyConnectedChild.title,
              subTitle: directlyConnectedChild.presentation,
              selectedAccessories: directlyConnectedChild?.selectedAccessories,
              iconName: directlyConnectedChild.iconName,
              x: childStartX,
              y: childStartY + nodeHeight * directlyConnectedChildIdx,
              isPrint,
            });

            interactiveElementsArr.push({
              ...directlyConnectedChild,
              interactiveKind: "node",
              startX: childStartX,
              startY: childStartY + nodeHeight * directlyConnectedChildIdx,
              zoneIdx,
            });

            zoneHeight += nodeHeight;
          },
        );
      }

      zoneHeight += nodeHeight + nodeGapY;
    });
  }

  //  Add Component button
  if (!isPrint && addComponentBtn?.submenu?.length) {
    const {
      isPasteEnabled: isPasteFamChildEnabled,
      isPastConfigEnabled: isPastConfigFamChildEnabled,
    } = !isPrint && getValidActions(menu);

    const interactiveKind = getInteractiveElemKind(
      isPasteFamChildEnabled,
      isPastConfigFamChildEnabled,
    );

    interactiveElementsArr.push({
      interactiveKind:
        isPasteFamChildEnabled && selectedTopologyItemKind === "component"
          ? interactiveKind
          : "addBtn",
      startX: famStartX + nodeWidth + rightChildrenGapX,
      startY: zoneHeight + addBtnHeight / 2 + addBtnMarginTop,
      zoneIdx,
      submenu: addComponentBtn?.submenu,
      topologyNodeId: id,
      topologyAction: addComponentBtn.id,
    });

    twoAngleLines.push({
      from: { x: famStartX + nodeWidth, y: famStartY + nodeHeight / 2 },
      to: {
        x: famStartX + nodeWidth + rightChildrenGapX,
        y: zoneHeight + addBtnHeight / 2 + addBtnMarginTop,
      },
      lineIdx: rightPlacedChildrenCount,
      linesCount: rightPlacedChildrenCount,
      stroke: gray400,
    });

    zoneHeight += addBtnHeight;
  }

  // Margin bellow FAM
  zoneHeight += bottomChildrenGapY;

  // Splitters and right placed children
  const addSplitterChildModuleBtn =
    !isPrint && menu.find((btn) => btn.id === 11);

  const famAllBottomChildrenCount =
    (bottomPlacedChildren?.length || 0) +
    (chainedChildren ? 1 : 0) +
    (addSplitterChildModuleBtn?.submenu?.length ? 1 : 0);

  if (bottomPlacedChildren) {
    bottomPlacedChildren?.forEach((splitter, splitterIdx) => {
      const { isPasteEnabled } = !isPrint && getValidActions(splitter.menu);
      const isWireless = splitter?.parentConnectionType === "WIRELESS";
      const addSplitterChildBtn =
        !isPrint && splitter.menu?.find((btn) => btn.id === 12);

      const splitterStartX = famStartX + 78;
      const splitterStartY = zoneHeight;

      nodes.push({
        id: splitter.id,
        kind: splitter.kind,
        title: splitter.title,
        subTitle: splitter.presentation,
        selectedAccessories: splitter?.selectedAccessories,
        iconName: splitter.iconName,
        x: splitterStartX,
        y: splitterStartY,
        isPrint,
      });

      !isPrint &&
        interactiveElementsArr.push({
          ...splitter,
          interactiveKind: "node",
          startX: splitterStartX,
          startY: splitterStartY,
          zoneIdx,
        });

      bottomLeftAngleLines.push({
        from: { x: famStartX + nodeHeight / 2, y: famStartY + nodeHeight },
        to: { x: splitterStartX, y: splitterStartY + nodeHeight / 2 },
        lineIdx: splitterIdx,
        linesCount: famAllBottomChildrenCount,
        isWireless,
      });

      const splitterRightPlacedChildrenCount =
        (splitter?.rightPlacedChildren?.length || 0) +
        (!!addSplitterChildBtn?.submenu?.length && 1);

      if (splitter?.rightPlacedChildren) {
        splitter.rightPlacedChildren.forEach(
          (splitterChild, splitterChildIdx) => {
            const {
              isPasteEnabled: isPasteSplitterChildEnabled,
              isPastConfigEnabled: isPastConfigSplitterChildEnabled,
            } = !isPrint && getValidActions(splitterChild.menu);

            const interactiveKind = getInteractiveElemKind(
              isPasteSplitterChildEnabled,
              isPastConfigSplitterChildEnabled,
            );

            const childStartX = famStartX + nodeWidth + rightChildrenGapX;
            const childStartY = zoneHeight;

            nodes.push({
              id: splitterChild.id,
              kind: splitterChild.kind,
              title: splitterChild.title,
              subTitle: splitterChild.presentation,
              selectedAccessories: splitterChild?.selectedAccessories,
              iconName: splitterChild.iconName,
              x: childStartX,
              y: childStartY,
              isPrint,
            });

            interactiveElementsArr.push({
              ...splitterChild,
              interactiveKind,
              startX: childStartX,
              startY: isPasteSplitterChildEnabled
                ? childStartY + nodeHeight / 2
                : childStartY,
              zoneIdx,
              topologyNodeId: splitterChild.id,
            });

            twoAngleLines.push({
              from: {
                x: splitterStartX + nodeWidth,
                y: splitterStartY + nodeHeight / 2,
              },
              to: { x: childStartX, y: childStartY + nodeHeight / 2 },
              lineIdx: splitterChildIdx,
              linesCount: splitterRightPlacedChildrenCount,
            });

            zoneHeight += nodeHeight + nodeGapY;
          },
        );
      }

      //  Add Splitter Child Button
      if (addSplitterChildBtn?.submenu?.length || isPasteEnabled) {
        interactiveElementsArr.push({
          interactiveKind: isPasteEnabled ? "pastBtn" : "addBtn",
          startX: famStartX + nodeWidth + rightChildrenGapX,
          startY: zoneHeight + addBtnHeight / 2 + addBtnMarginTop,
          submenu: addSplitterChildBtn?.submenu || [],
          zoneIdx,
          topologyNodeId: splitter.id,
          topologyAction: 12,
          isPasteEnabled,
        });

        twoAngleLines.push({
          from: {
            x: splitterStartX + nodeWidth,
            y: splitterStartY + nodeHeight / 2,
          },
          to: {
            x: famStartX + nodeWidth + rightChildrenGapX,
            y: zoneHeight + addBtnHeight / 2 + addBtnMarginTop,
          },
          lineIdx: splitterRightPlacedChildrenCount - 1,
          linesCount: splitterRightPlacedChildrenCount,
          stroke: gray400,
        });
      }

      //  Margin below splitter
      zoneHeight += addBtnHeight + bottomChildrenGapY;
    });
  }

  // Add module bellow
  if (!isPrint) {
    const { isPasteEnabled: isPasteCMEnabled } =
      !isPrint && getValidActions(menu);

    if (addSplitterChildModuleBtn?.submenu?.length) {
      interactiveElementsArr.push({
        interactiveKind:
          isPasteCMEnabled && selectedTopologyItemKind === "can"
            ? "pastBtn"
            : "addBtn",
        startX: famStartX + addBtnHeight + addBtnMarginLeft,
        startY: zoneHeight + addBtnMarginTop,
        submenu: addSplitterChildModuleBtn?.submenu || [],
        topologyNodeId: id,
        topologyAction: 11,
        zoneIdx,
      });

      bottomLeftAngleLines.push({
        from: { x: famStartX + nodeHeight / 2, y: famStartY + nodeHeight },
        to: {
          x: famStartX + addBtnHeight + addBtnMarginLeft,
          y: zoneHeight + addBtnMarginTop,
        },
        lineIdx: bottomPlacedChildren?.length || 0,
        linesCount: famAllBottomChildrenCount,
        stroke: gray400,
      });

      zoneHeight += addBtnHeight;
    }
  }

  if (famData?.chainedChildren?.length) {
    const connectionModuleStartX = famStartX + nodeHeight;
    zoneHeight += bottomChildrenGapY;

    chainedChildren?.forEach((chainedChild) => {
      const { isPasteEnabled } = !isPrint && getValidActions(chainedChild.menu);

      const connectionModuleStartY = zoneHeight;

      const drawModuleAndRightChildren = ({ startX, startY, moduleData }) => {
        nodes.push({
          id: moduleData.id,
          kind: "connectionModule",
          title: moduleData.title,
          subTitle: moduleData.presentation,
          selectedAccessories: moduleData?.selectedAccessories,
          iconName: moduleData.iconName,
          x: startX,
          y: startY,
          isPrint,
        });

        interactiveElementsArr.push({
          ...moduleData,
          interactiveKind: "node",
          startX,
          startY,
          zoneIdx,
        });

        const connectionModuleChildrenCount =
          (chainedChildren?.length || 0) +
          (!!chainedChildren?.submenu?.length && 1) +
          (!!isPasteEnabled && 1);

        moduleData?.rightPlacedChildren?.forEach((child, childIdx) => {
          if (child.kind === "can") {
            return;
          }

          const childStartX = famStartX + nodeWidth + rightChildrenGapX;
          const childStartY = zoneHeight;

          const {
            isPasteEnabled: isPasteCMChildEnabled,
            isPastConfigEnabled: isPastConfigCMChildEnabled,
          } = !isPrint && getValidActions(child.menu);

          const interactiveKind = getInteractiveElemKind(
            isPasteCMChildEnabled,
            isPastConfigCMChildEnabled,
          );

          nodes.push({
            id: child.id,
            kind: child.kind,
            title: child.title,
            subTitle: child.presentation,
            selectedAccessories: child?.selectedAccessories,
            iconName: child.iconName,
            x: childStartX,
            y: childStartY,
            isPrint,
          });

          interactiveElementsArr.push({
            ...child,
            interactiveKind,
            startX: childStartX,
            startY: childStartY,
            zoneIdx,
            topologyNodeId: child.id,
          });

          const lineStartY =
            child.rightPlacedChildren?.length > 1
              ? startY +
                (nodeHeight - nodeLinesGap * connectionModuleChildrenCount) /
                  2 +
                nodeLinesGap * childIdx
              : startY + nodeHeight / 2;

          twoAngleLines.push({
            from: { x: startX + nodeWidth, y: lineStartY },
            to: { x: childStartX, y: childStartY + nodeHeight / 2 },
            lineIdx: childIdx,
            linesCount: connectionModuleChildrenCount,
          });

          zoneHeight += nodeHeight + nodeGapY;
        });

        if (!isPrint) {
          const addChainedChildBtn = moduleData.menu?.find(
            (btn) => btn.id === 12,
          );

          if (addChainedChildBtn?.submenu?.length) {
            const addBtnStartX = famStartX + nodeWidth + rightChildrenGapX;
            zoneHeight += addBtnMarginTop - 2;

            interactiveElementsArr.push({
              interactiveKind: isPasteEnabled ? "pastBtn" : "addBtn",
              startX: addBtnStartX,
              startY: zoneHeight,
              submenu: addChainedChildBtn?.submenu || [],
              topologyNodeId: moduleData.id,
              topologyAction: 12,
              zoneIdx,
              isPasteEnabled,
            });

            twoAngleLines.push({
              from: {
                x: connectionModuleStartX + nodeWidth,
                y: connectionModuleStartY + nodeHeight / 2,
              },
              to: { x: addBtnStartX, y: zoneHeight },
              lineIdx: connectionModuleChildrenCount,
              linesCount: connectionModuleChildrenCount,
              stroke: gray400,
            });
          }
        }
      };

      drawModuleAndRightChildren({
        startX: connectionModuleStartX,
        startY: zoneHeight,
        moduleData: chainedChild,
      });

      bottomLeftAngleLines.push({
        from: { x: famStartX + nodeHeight / 2, y: famStartY + nodeHeight },
        to: {
          x: connectionModuleStartX - 1,
          y: connectionModuleStartY + nodeHeight / 2 - nodeLinesGap / 2,
        },
        lineIdx: famAllBottomChildrenCount - 1,
        linesCount: famAllBottomChildrenCount,
      });

      if (chainedChild?.directlyConnected) {
        leftBracketPolyLines.push({
          from: {
            x: connectionModuleStartX - 1,
            y: connectionModuleStartY + nodeHeight / 2 + nodeLinesGap,
          },
          to: {
            x: connectionModuleStartX,
            y: zoneHeight + nodeHeight / 2 - nodeLinesGap,
          },
          leftGap: 14,
        });
        chainedChild.directlyConnected.forEach((moduleData, index, array) => {
          const startModuleY = zoneHeight;
          drawModuleAndRightChildren({
            startX: connectionModuleStartX,
            startY: zoneHeight,
            moduleData,
          });
          if (index !== array.length - 1) {
            leftBracketPolyLines.push({
              from: {
                x: connectionModuleStartX - 1,
                y: startModuleY + nodeHeight / 2,
              },
              to: {
                x: connectionModuleStartX,
                y: zoneHeight + nodeHeight / 2 - nodeLinesGap,
              },
              leftGap: 14,
            });
          }
        });
      }

      zoneHeight += nodeHeight + nodeGapY;
    });
  }

  // setTopologyWidth(
  //   famStartX + (nodeWidth + rightChildrenGapX) * 2 + nodeWidth * 0.5,
  // );
  // setTopologyHeight(Math.max(zoneHeight, topologyHeight));

  return {
    zoneHeight: zoneHeight + bottomChildrenGapY,
    nodes,
    texts,
    twoAngleLines,
    bottomLeftAngleLines,
    leftBracketPolyLines,
    interactiveElementsArr,
  };
};
